/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filterregel-functie-edit.component.ts
 * Gegenereerd met Junie (AI), 05-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Editorcomponent voor functie-regels met multiselect op Functie.
 */

import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {FilterregelEditComponent} from './filterregel-edit.component';
import {Functie} from '../../../models/functie';
import {FiltertoolPlanitemfunctieRegel} from '../../../services/filtertool/regels/filtertool-functieregel';
import {FiltertoolRegelTypeNamen} from '../../../services/filtertool/filtertool-regels';
import {FilterregelActiesComponent} from './filterregel-acties.component';

/**
 * Editor voor functieregels: selecteer 1 of meer functies (multiselect) en beheer verwijderen.
 */
@Component({
    standalone: true,
    selector: 'filterregel-functie-edit',
    imports: [CommonModule, FormsModule, NgSelectModule, FilterregelActiesComponent],
    templateUrl: './filterregel-functie-edit.component.html',
    styleUrls: ['./filterregel-functie-edit.component.scss', './filterregel-editors.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterregelFunctieEditComponent extends FilterregelEditComponent<FiltertoolPlanitemfunctieRegel> implements OnInit {
    // DI-services
    // Inputs (properties)
    /** De te bewerken functieregel. */
    @Input({required: true}) override regel!: FiltertoolPlanitemfunctieRegel;

    // Properties
    /** Mapping met label/omschrijving per regeltype voor weergave. */
    public readonly regelTypeNamen = FiltertoolRegelTypeNamen;
    /** Alle beschikbare functies (lookup). */
    public functies: Functie[] = [];
    /** Lokale binding van gekozen ids voor ngModel. */
    public gekozenFunctieIds: number[] = [];

    // Constructor
    constructor() {
        super('debug');
    }

    // Lifecycle hooks
    async ngOnInit() {
        //init selectie
        this.gekozenFunctieIds = Array.isArray(this.regel?.functieIds) ? [...this.regel.functieIds] : [];
        //haal functies op
        this.functies = await Functie.getAll([], [[Functie, {
            attributes: ['id', 'naam']
        }]]);
        // trigger hertekenen omdat OnPush + @if anders niet update na async load
        this.cd.markForCheck();
        this.log.debug(`Functies`, this.functies);
        this.log.debug(`Regel`, this.regel);
        this.log.debug(`Mapping`, this.gekozenFunctieIds.map(id => this.functies.find(f => f.id == id)?.naam).join(', '))
        this.log.debug(`is regel geldig?`, this.isRegelGeldig);
    }

    // Public methods
    /**
     * Slaat gewijzigde selectie op in de regel en emit de wijziging.
     */
    public functiesGewijzigd(): void {
        //cleanup: verwijder null/undefined
        const clean = (this.gekozenFunctieIds || []).filter(v => v != null);
        //schrijf direct in de regel
        if (this.regel) {
            // immutabele toekenning zodat getters direct goed reageren
            this.regel.functieIds = [...clean];
            // emit optioneel wijzig-event voor dirty-state
            this.wijzig.emit(this.regel);
        }
        // OnPush: forceer directe CD (ng-select kan buiten Angular zone emiteren)
        try {
            this.cd.detectChanges();
        } catch {
            this.cd.markForCheck();
        }
        this.log.debug(`is regel geldig?`, this.isRegelGeldig);
    }
}
